import { ref } from 'vue';

/**
 * Represents the raw image size computation result
 */
export type RawImageSize = {
  size: number;
  shouldFetch: boolean;
};

/**
 * Dependencies for the visibility observer handler
 */
export interface VisibilityObserverDeps {
  imgElement: { value: HTMLImageElement | null };
  rawImgSize: { value: number };
  defaultCellSize: number;
  onImageSizeDetermined: (size: number) => void;
  onRefreshDrawing: () => void;
}

/**
 * Compute raw image size from element or determine if fetch is needed
 */
export function computeRawImageSize(
  imgElement: HTMLImageElement | null,
  currentRawSize: number,
  defaultSize: number
): RawImageSize {
  // Prefer naturalWidth whenever available, even if the image isn't fully complete yet
  if (imgElement?.naturalWidth) {
    return {
      size: imgElement.naturalWidth,
      shouldFetch: false,
    };
  }
  
  if (currentRawSize === defaultSize) {
    return {
      size: currentRawSize,
      shouldFetch: true,
    };
  }
  
  return {
    size: currentRawSize,
    shouldFetch: false,
  };
}

/**
 * Handle intersection observer callback for cell visibility
 * This is the core logic extracted from the component for easier testing
 */
export function handleCellVisibilityChange(
  isIntersecting: boolean,
  deps: VisibilityObserverDeps
): void {
  if (!isIntersecting) {
    return;
  }

  // Update image size if already loaded in cache
  const sizeResult = computeRawImageSize(
    deps.imgElement.value,
    deps.rawImgSize.value,
    deps.defaultCellSize
  );

  if (sizeResult.size !== deps.rawImgSize.value) {
    deps.onImageSizeDetermined(sizeResult.size);
  }

  if (sizeResult.shouldFetch) {
    // Signal that size needs to be fetched asynchronously
    // The component will call getRawImgSize() based on this flag
  }

  deps.onRefreshDrawing();
}

/**
 * Composable for managing cell visibility with intersection observer
 * Simplifies testing by separating concerns
 */
export function useCellVisibility(
  imgElement: { value: HTMLImageElement | null },
  defaultCellSize: number = 153.6
) {
  const rawImgSize = ref<number>(defaultCellSize);
  const isVisible = ref(false);

  const handleVisibilityChange = (isIntersecting: boolean, callbacks: {
    onImageSizeDetermined: (size: number) => void;
    onRefreshDrawing: () => void;
  }) => {
    isVisible.value = isIntersecting;
    
    handleCellVisibilityChange(isIntersecting, {
      imgElement,
      rawImgSize,
      defaultCellSize,
      onImageSizeDetermined: callbacks.onImageSizeDetermined,
      onRefreshDrawing: callbacks.onRefreshDrawing,
    });
  };

  return {
    isVisible,
    rawImgSize,
    handleVisibilityChange,
  };
}
